import React from 'react';
import withFocus from '../../hocs/withFocus';
import withToggle from '../../hocs/withToggle';
import withFormErrors from '../../hocs/withFormErrors';
import ListBase from '../ListBase';
import AutoInput from './AutoInput';
import SuggestionList from './SuggestionList';
import useComboboxState from './utils/useComboboxState';
import useComboboxWrapperStyles from './utils/useComboboxWrapperStyles';
import useCloseOnOutsideClick from './utils/useCloseOnOutsideClick';
import detectAutofill, { useOnAnimationStart, useOnInput } from '../../utils/detectAutofill';
import useDetectAutofill from './utils/useDetectAutofill';
// This component can be used with both static and dynamic suggestion list
// For a static list, just pass the `suggestions` list
// Otherwise, pass the `handleFetchSuggestions` prop and make sure that
// `suggestions` change in response to `handleFetchSuggestions` invocation
// maxLength caveat
// TLDR; set yup the `max` validation for this field on the Form component as well
// maxLength is set by default to 100 characters to prevent user from overloading server
// you can set it some other value, but you must repeat the same validation on the
// Form component since react-hook-form's `setError` method currently does not persist
// error, thus it cannot be used to show the error message to a user (our forms must be
// validated on each keystorke so that submit buttons can only be enabled when valid)
// https://react-hook-form.com/api/#setError
export const Combobox = ({ suggestions, handleFetchSuggestions, name, displayName, placeholder, onChange, onSelection, required = false, disabled = false, noExtraMargin, isToggled, setOn, setOff, isFocused, handleFocus, handleBlur, styles, maxLength, dropdownDirection = 'down', preventSuggestionFilter = false, errorId, errorMessage, onlyValidateWithError, showCreateNewOption = false, isFetchingSuggestions = false }) => {
    const [isAutofilled, setIsAutofilled] = React.useState(false);
    const { handleKeyDown, handleChange, hasSuggestions, suggestionOptions, showCreateNewLabel } = useComboboxState({
        name,
        maxLength,
        handleFetchSuggestions,
        suggestions,
        onSelection,
        onChange,
        isToggled,
        setOff,
        setOn,
        preventSuggestionFilter,
        onlyValidateWithError,
        showCreateNewOption
    });
    const onAutofill = (event) => {
        const target = event.target;
        target.hasAttribute('data-autocompleted') && target.name === name && setIsAutofilled(true);
    };
    React.useEffect(() => {
        detectAutofill(name, useOnAnimationStart, useOnInput, onAutofill);
    }, []);
    useDetectAutofill({ isToggled, isAutofilled, setOff, setIsAutofilled });
    const { ref } = useCloseOnOutsideClick({ isToggled, setOff });
    const wrapperStyles = useComboboxWrapperStyles({
        styles,
        isToggled,
        hasSuggestions,
        dropdownDirection
    });
    return (React.createElement(ListBase, { type: "autocomplete", name: name, displayName: displayName, required: required, disabled: disabled, isFocused: isFocused, isToggled: isToggled, noExtraMargin: noExtraMargin, styles: wrapperStyles, errorId: errorId, errorMessage: errorMessage },
        React.createElement("div", { ref: ref, "aria-haspopup": "listbox" },
            React.createElement(AutoInput, { name: name, placeholder: placeholder, handleChange: handleChange, isFocused: isFocused, handleFocus: handleFocus, handleBlur: handleBlur, handleKeyDown: handleKeyDown, showCreateNewLabel: showCreateNewLabel, isLoading: isFetchingSuggestions }),
            React.createElement(SuggestionList, { isFocused: isFocused, isToggled: isToggled && hasSuggestions, dropdownDirection: dropdownDirection }, suggestionOptions))));
};
export default withFormErrors(withToggle(withFocus(Combobox)));
//# sourceMappingURL=index.js.map